// The ECONIC content card (APPLE-SPEC §7 10.9): opaque white (dark #232325), radius 8, --shadow-card (it carries the 0.5 px edge) —
// no border, glow, gold rim or gradient; header ≥ 38 px (padding 9 12 3, title 13/600 wraps, never truncates, no divider; `ruledHeader`
// adds one), body 8 12 12 (tight 6 12 10, flush 0), footer ≥ 32 px on a hairline for the module tag, source stamps and the end link. Snippets on surfaces use the surface engine's WidgetFrame (same anatomy, plus states and the ⋯ menu); this is the plain card
// for everything else (Trust Centre sections, admin tables, KB panels). WP-THEME.
import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./lang";

export interface EconicCardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title?: ReactNode;
  /** Heading level of the title (defaults to h3). */
  as?: "h2" | "h3" | "h4";
  /** A short sub-line after the title ("updated 29 Sep", "7 mine · 23 department"). */
  sub?: ReactNode;
  /** Chips and actions at the end of the header. */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Body padding: default 8/12/12, tight 6/12/10, flush 0 (tables). */
  body?: "default" | "tight" | "flush";
  /** Kept for compatibility: every card is opaque now (§00.6). */
  solid?: boolean;
  /** Kept for compatibility (there is no gold rim to drop any more); with `flat` the card also loses its 1 px shadow. */
  flat?: boolean;
  /** The 3 s ring after an agent edit (a 2 px --tint-wash-2 ring, static under reduced motion). */
  highlighted?: boolean;
  /** A hairline under the header (tables and lists that start at the top edge). */
  ruledHeader?: boolean;
}

export function EconicCard({
  title,
  as: H = "h3",
  sub,
  actions,
  footer,
  body = "default",
  solid,
  flat,
  highlighted,
  ruledHeader,
  className,
  children,
  ...rest
}: EconicCardProps) {
  return (
    <section className={cx("ec-card", solid && "solid", flat && "plain", highlighted && "hl", className)} {...rest}>
      {(title || actions) && (
        <header className={cx("ec-card-h", ruledHeader && "ruled")}>
          {title && <H>{title}</H>}
          {sub && <span className="sub">{sub}</span>}
          {actions}
        </header>
      )}
      <div className={cx("ec-card-b", body !== "default" && body)}>{children}</div>
      {footer && <footer className="ec-card-f">{footer}</footer>}
    </section>
  );
}

/** The §00 name of EconicCard. */
export const Card = EconicCard;
export type CardProps = EconicCardProps;
